import type { AppError, Result } from '../types';

/**
 * Represents a successful `Result` containing a value.
 *
 * @typeParam T - The type of the success value.
 * @typeParam E - The type of the error (defaults to `never`).
 */
export class Ok<T, E = AppError> {
  /**
   * Indicates that this is an `Ok` result.
   */
  readonly isOk = true;

  /**
   * Indicates that this is not an `Err` result.
   */
  readonly isErr = false;

  /**
   * The wrapped success value.
   */
  constructor(public readonly value: T) {}

  /**
   * Maps the success value to a new `Result` by applying the given function.
   *
   * @typeParam U - The type of the mapped value.
   * @param fn - The function to apply to the success value.
   * @returns A new `Ok` containing the mapped value.
   */
  map<U>(fn: (val: T) => U): Result<U, E> {
    return new Ok(fn(this.value));
  }

  /**
   * Maps the error value. Since this is `Ok`, the error is ignored and the same `Ok` is returned.
   *
   * @typeParam U - The new error type.
   * @param _fn - Ignored.
   * @returns The same `Ok` instance.
   */
  mapErr<U>(_fn: (err: E) => U): Result<T, U> {
    return new Ok(this.value);
  }

  /**
   * Chains another operation that returns a `Result` if this is `Ok`.
   *
   * @typeParam U - The type of the result from the chained operation.
   * @param fn - The function to apply to the success value.
   * @returns The `Result` returned by the function.
   */
  andThen<U>(fn: (val: T) => Result<U, E>): Result<U, E> {
    return fn(this.value);
  }

  /**
   * Gets the success value.
   *
   * @returns The wrapped success value.
   */
  getSuccess(): T {
    return this.value;
  }

  /**
   * Gets the success value, ignoring the provided default.
   *
   * @param _default - Ignored.
   * @returns The wrapped success value.
   */
  getDefault(_default: T): T {
    return this.value;
  }

  /**
   * Gets the error value. Since this is `Ok`, this throws an error.
   *
   * @throws Always throws because `Ok` does not contain an error.
   */
  getFail(): never {
    throw new Error('Tried to unwrap Err value on an Ok.');
  }

  /**
   * Pattern-matches on the `Result`.
   *
   * @typeParam U - The return type.
   * @param handlers - Object containing `ok` and `err` handlers.
   * @returns The result of the `ok` handler.
   */
  match<U>(handlers: { ok: (value: T) => U; err: (error: E) => U }): U {
    return handlers.ok(this.value);
  }
}
